import React, { useMemo } from 'react';
import { Chip, Stack } from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import { alpha } from '@mui/material/styles';
import { useResources } from '@platform/core/src/context/ResourceContext';

const formatMeasurements = (actions) => {
  if (!actions || !Array.isArray(actions) || actions.length === 0) return '';
  const summaryParts = [];
  actions.forEach(action => {
    const measurements = action.measurements || action.prescribed_measurements || [];
    const reps = measurements.find(m => m.type === 'REPETITIONS' || m.type === 'reps');
    const weight = measurements.find(m => m.type === 'WEIGHT' || m.type === 'weight');
    const duration = measurements.find(m => m.type === 'DURATION' || m.type === 'duration');
    const distance = measurements.find(m => m.type === 'DISTANCE' || m.type === 'distance');

    const parts = [];
    if (reps && reps.value) parts.push(`${reps.value} reps`);
    if (weight && weight.value) parts.push(`${weight.value} ${weight.unit || 'lbs'}`);
    if (duration && duration.value) parts.push(`${duration.value} ${duration.unit || 'min'}`);
    if (distance && distance.value) parts.push(`${distance.value} ${distance.unit || 'mi'}`);

    if (parts.length > 0) {
      summaryParts.push(parts.join(' @ '));
    }
  });
  return summaryParts.join(', ');
};

export const getWorkoutSessionChanges = (event, session, allDefinitions = []) => {
    const { patches, change_type, resource_id } = event;
    const changes = [];

    if (change_type === 'created') {
        changes.push({
            id: `created-${resource_id}`,
            label: "New Workout Session Created",
            icon: <AddCircleOutlineIcon fontSize="small" />,
            color: "primary"
        });
        return changes;
    }

    if (change_type === 'updated' && (!patches || patches.length === 0)) {
         changes.push({
            id: `updated-${resource_id}-${Date.now()}`,
            label: "Session Synced",
            icon: <SyncIcon fontSize="small" />,
            color: "default"
        });
        return changes;
    }

    if (!patches || !Array.isArray(patches)) return [];

    const resolveActivityName = (activityObj) => {
      if (!activityObj) return 'Activity';
      if (activityObj.name) return activityObj.name;
      if (activityObj.exercise_definition_id) {
          const def = allDefinitions.find(d => String(d.id) === String(activityObj.exercise_definition_id));
          if (def) return def.name;
      }
      return 'Activity';
    };

    patches.forEach((patch) => {
      const path = patch.path;

      if (path === '/status') {
          const status = patch.value;
          
          // Filter out redundant status updates (e.g. editing a completed session)
          if (session && session.status === status) {
              return;
          }

          let label = `Session Status: ${status}`;
          let icon = <HistoryIcon fontSize="small" />;
          let color = "info";

          if (status === 'COMPLETED') {
              label = "Workout Session Completed";
              icon = <CheckCircleIcon fontSize="small" />;
              color = "success";
          } else if (status === 'IN_PROGRESS') {
              label = "Workout Started";
              color = "primary";
          }
          
          changes.push({
              id: `status-${status}-${Math.random()}`,
              label,
              icon,
              color
          });
          return;
      }

      // Handle full activity list replacement (bulk edit)
      if (path === '/activities' && patch.op === 'replace') {
           const exists = changes.find(c => c.id === 'generic-update');
           if (!exists) {
               changes.push({
                   id: 'generic-update',
                   label: `Workout Session Edited`,
                   icon: <EditIcon fontSize="small" />,
                   color: "default"
               });
           }
           return;
      }

      if (patch.op === 'add' && /^\/activities\/(\d+|-)$/.test(path)) {
          const activity = patch.value;
          const activityName = resolveActivityName(activity);
          const details = formatMeasurements(activity.actions);
          changes.push({
              id: `log-act-${path}`,
              label: details ? `Logged: ${activityName} - ${details}` : `Logged: ${activityName}`,
              icon: <FitnessCenterIcon fontSize="small" />,
              color: "success"
          });
          return;
      }

      const setMatch = path.match(/^\/activities\/(\d+)\/actions\/(\d+|-)$/);
      if (patch.op === 'add' && setMatch) {
          const activityIndex = parseInt(setMatch[1], 10);
          let activityName = 'Activity';
          if (session && session.activities && session.activities[activityIndex]) {
              activityName = resolveActivityName(session.activities[activityIndex]);
          }
          const action = patch.value;
          const details = formatMeasurements([action]);
          changes.push({
              id: `log-set-${path}`,
              label: details ? `Logged: ${activityName} - ${details}` : `Logged: ${activityName}`,
              icon: <FitnessCenterIcon fontSize="small" />,
              color: "success"
          });
          return;
      }

      if (patch.op === 'replace' && /^\/activities\/\d+/.test(path)) {
          if (path.endsWith('/name')) {
              changes.push({
                  id: `upd-name-${path}`,
                  label: `Renamed Activity to: ${patch.value}`,
                  icon: <EditIcon fontSize="small" />,
                  color: "default"
              });
          }
          else if (path.includes('/value') || path.includes('/unit')) {
               const exists = changes.find(c => c.id === 'generic-update');
               if (!exists) {
                   changes.push({
                       id: 'generic-update',
                       label: `Updated Log Details`,
                       icon: <EditIcon fontSize="small" />,
                       color: "default"
                   });
               }
          }
      }
    });

    return changes;
};

const WorkoutSessionEffectRenderer = ({ event }) => {
  const { resource_id } = event;
  const { getResourceById, getResourcesByType } = useResources();
  const allDefinitions = useMemo(() => getResourcesByType('exercise_definition'), [getResourcesByType]);
  const session = getResourceById(resource_id);

  const changesToRender = useMemo(() => {
      return getWorkoutSessionChanges(event, session, allDefinitions);
  }, [event, session, allDefinitions]);

  if (changesToRender.length === 0) return null;

  return (
    <Stack direction="column" spacing={0.5} sx={{ width: '100%', my: 0.5 }}>
        {changesToRender.map((change) => {
            const getColor = (theme) => {
                if (!change.color || change.color === 'default') {
                    return theme.palette.text.secondary;
                }
                return theme.palette[change.color]?.main || theme.palette.primary.main;
            };

            return (
            <Chip
                key={change.id}
                icon={change.icon}
                label={change.label}
                size="small"
                variant="outlined"
                color={change.color === 'default' ? 'default' : (change.color || 'default')}
                sx={{
                    maxWidth: '100%',
                    justifyContent: 'flex-start',
                    bgcolor: (theme) => alpha(getColor(theme), 0.05),
                    borderColor: (theme) => alpha(getColor(theme), 0.3),
                    '& .MuiChip-label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 500
                    }
                }}
            />
            );
        })}
    </Stack>
  );
};

WorkoutSessionEffectRenderer.shouldRender = (event) => {
    const changes = getWorkoutSessionChanges(event, null, []);
    return changes.length > 0;
};

export default WorkoutSessionEffectRenderer;